import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import Checkbox from '../components/psc/lib/Checkbox'
import SaveButton from '../components/psc/lib/SaveButton'
import ErrorMessage from '../components/psc/lib/ErrorMessage'
import CancelButton from '../components/psc/lib/CancelButton'
import SingleSelect from '../components/psc/lib/SingleSelect'
import Applicability from '../components/psc/lib/Applicability'

import useAuthentication from '../hooks/useAuthentication';
import useCadmObject from '../hooks/useCadmObject';
import useUserManagement from '../hooks/useUserManagement';
import useProjectContents from '../hooks/useProjectContents';

const IncludedNewDocument = () => {
    const navigate = useNavigate()
    const pscState = useSelector(state => state.pscState.value);
    const project = pscState.project
    const params = useParams();
    const projectID = Number(params.projectID);

    const { currentUser } = useAuthentication();

    const [stdDocumentID, setStdDocumentID] = useState(0)
    const [documentModels, setDocumentModels] = useState([])

    const [fromProjectID, setFromProjectID] = useState(0)
    const [includedDocumentIDs, setIncludedDocumentIDs] = useState([])
    const [projects, setProjects] = useState([])
    const [original, setOriginal] = useState({ id: 0, std_document: { name: '' }})
    const [originalDocuments, setOriginalDocuments] = useState([])
    const [documentSelectPlaceholder, setDocumentSelectPlaceholder] = useState('Select...')
    const [error, setError] = useState([])
    const [submitActive, setSubmitActive] = useState(false)
    const [submitSpinner, setSubmitSpinner] = useState(false)
    const [addAnother, setAddAnother] = useState(false)

    const {
        userHasProjectAccessLevel,
    } = useUserManagement()

    const {
        getStdDocument,
        getUnarchivedStdDocuments,
    } = useCadmObject('documents')

    const {
        updateIncludedDocument,
    } = useProjectContents()
    
    useEffect(() => {
        // Load all projects we can pick documents from
        // Also load currently included docs, so we filter them out in the selection process
        
        fetch('/v0/projects?do_allow_document_sharing=1&is_archived=0&user_has_explicit_access=1', { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                const projects = json.results.filter(item => item.id !== projectID)
                setProjects(projects)
            })
        
        fetch('/v0/included_documents?project_id='+projectID, { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                setIncludedDocumentIDs(json.results.map(item => item.original_document_id))
            })
    }, [projectID])

    const handleChangeProject = (project) => {
        // Load all documents we can pick
        let id = project.id

        if( ! project.id > 0 ) {
            return
        }

        setFromProjectID(id)
        setStdDocumentID(0)
        setOriginal({ id: 0, std_document: { name: '' }})
        setOriginalDocuments([])
        setDocumentSelectPlaceholder('Loading...')
        
        fetch('/v0/documents?project_id='+id, { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                const original_documents = json.results
                                               .filter(item => {
                                                   return ! includedDocumentIDs.find(id => id === item.id)
                                               })
                                               .map(item => {
                                                   item.label = item.reference + ' ' + item.title + ' [' + item.document_status.name + ']'
                                                   return item
                                               })
                
                setOriginalDocuments(original_documents)
                setDocumentSelectPlaceholder('Select...')
            })
    }
    
    const handleChangeOriginalDocument = (document) => {
        let id = document.id

        if( ! id > 0 ) {
            return
        }
        setOriginal(document)
        fetch('/v0/documents/' + id, { credentials: 'same-origin' })
            .then(response => response.json())
            .then(json => {
                const original = json.result
                setOriginal(original)
                const item = getStdDocument(original.std_document_id)
                if( item ) {
                    setStdDocumentID(item.std_document_id)
                } else {
                    setStdDocumentID(0)
                }
            })
    }
    
    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setSubmitSpinner(false)
        setError([ props.message ])
    }

    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }

        if( submitActive ) {
            return null
        }

        let newErrors = []

        if ( !fromProjectID ) {
            newErrors.push('Origin is required')
        }

        if ( !original.id ) {
            newErrors.push('Document is required')
        }

        if ( !stdDocumentID ) {
            newErrors.push('Type is required')
        }

        if ( newErrors.length > 0 ) {
            setError(newErrors)
            return null
        }

        setError([])
        setSubmitActive(true)
        
        const data = {
            project_id: projectID,
            original_document_id: original.id,
            std_document_id: stdDocumentID,
            document_models: documentModels,
        }
        let submit = {
            objType: 'included_documents',
            objID: 'x', // d'oh
            data: data,
            successCallback: function(json) {
                updateIncludedDocument(json.result)
            }
        }

        if ( addAnother ) {
            let included_document_ids = includedDocumentIDs
            included_document_ids.push(data.original_document_id)
            const original_documents = originalDocuments.filter(item => item.id !== data.original_document_id)
            
            // Clear all state (except selected project)
            submit.navigateOverride = function() { }
            submit.successCallback = function(json) {
                updateIncludedDocument(json.result)
                setOriginal({ id: 0, std_document: { name: '' } })
                setStdDocumentID(0)
                setDocumentSelectPlaceholder('Select...')
                setOriginalDocuments(original_documents)
                setAddAnother(false)
                setSubmitActive(false)
            }

            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
        return submit
    }

    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                        
                            <h2>New Included Document</h2>
                            
                            <div key="newText1" style={{color: 'rgba(103, 106, 108, 0.65)'}}>
                                Select a Document (and version) to Include, then define its Type and Applicability <b>for this {project.project_type.name}</b>. The original Document <b>is not changed</b>.
                            </div>
                            
                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(event) => handleSubmit(event)}>
                                
                                <div className="form-group">
                                    <label className="col-sm-2"></label>
                                    <div className="col-sm-8">
                                        <h3>Document to Include</h3>
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">
                                        Origin
                                        <label className="form-required-star">&nbsp;*</label>
                                    </label>
                                    <div className="col-sm-8">
                                        <SingleSelect
                                            value={fromProjectID}
                                            onChange={handleChangeProject}
                                            options={projects}
                                        />
                                        <span className="help-block m-b-none m-t-none">
                                            <small className="form-info-text" > Projects and Products must allow sharing to appear in this list.</small>
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">
                                        Document
                                        <label className="form-required-star">&nbsp;*</label>
                                    </label>
                                    <div className="col-sm-8">
                                        <SingleSelect
                                            name="original_document_id"
                                            value={original.id}
                                            onChange={handleChangeOriginalDocument}
                                            options={originalDocuments}
                                            placeholder={documentSelectPlaceholder}
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">
                                        Type
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" disabled={true}
                                            value={original.std_document.name} />
                                    </div>
                                </div>
                                
                                <div className="hr-line-dashed"></div>
                                
                                <div className="form-group">
                                    <label className="col-sm-2"></label>
                                    <div className="col-sm-8">
                                        <h3>Include in this {project.project_type.name} as</h3>
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">
                                        Type
                                        <label className="form-required-star">&nbsp;*</label>
                                    </label>
                                    <div className="col-sm-8">
                                        <SingleSelect
                                                value={stdDocumentID}
                                                onChange={(value) => setStdDocumentID(value.std_document_id)}
                                                options={getUnarchivedStdDocuments()}
                                                required
                                            />
                                    </div>
                                </div>
                                
                                <Applicability
                                    projectId={projectID}
                                    projectType={project.project_type.name}
                                    options={project.project_systems}
                                    value={documentModels}
                                    onChange={(value) => setDocumentModels(value)}
                                    disabled={!userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER')}
                                    subLabel={`(This ${project.project_type.name})`}
                                />
                                
                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />
                                    
                                    <div className="col-sm-10 col-sm-offset-2">
                                        <CancelButton />
                                        
                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={userHasProjectAccessLevel(currentUser,projectID, "CADM_CONTROLLER")}
                                            spinnerActive={submitSpinner}
                                            label='Include'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />

                                        &nbsp;

                                        <div className="form-check" style={{display: 'inline-block'}}>
                                            <Checkbox
                                                id="add-another-checkbox"
                                                name="add_another"
                                                isChecked={addAnother}
                                                onChange={() => setAddAnother(!addAnother)}
                                            />
                                            <label htmlFor="add-another-checkbox">
                                                Add Another
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default IncludedNewDocument
